import React, { useMemo } from 'react';
import { Spin, Alert } from 'antd';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PrintPreviewChrome } from '@/components/print/PrintPreviewChrome';
import { InspectionReleaseNotePrintView } from '@/components/quality/InspectionReleaseNotePrintView';
import { irnService } from '@/services/qc-workflow.service';
import type { InspectionReleaseNote } from '@/services/qc-workflow.service';
import { useUserDisplayResolver } from '@/hooks/useUserDisplayResolver';

const InspectionReleaseNotePrintPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { resolve } = useUserDisplayResolver();
  const printedAt = useMemo(() => new Date().toISOString(), []);

  const { data: irn, isLoading, isError, error } = useQuery({
    queryKey: ['irn-print', id],
    queryFn: async () => {
      if (!id) throw new Error('Missing id');
      const response = await irnService.getById(id);
      const raw = response.data?.data ?? response.data;
      return raw as InspectionReleaseNote;
    },
    enabled: !!id,
  });

  const back = () => {
    if (id) navigate(`/qms/inspection-release-notes/${id}`);
    else navigate('/qms/inspection-release-notes');
  };

  if (!id) {
    return <Navigate to="/qms/inspection-release-notes" replace />;
  }

  if (isLoading) {
    return (
      <PrintPreviewChrome title="IRN — print preview" onBack={back}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 120 }}>
          <Spin size="large" />
        </div>
      </PrintPreviewChrome>
    );
  }

  if (isError || !irn) {
    return (
      <PrintPreviewChrome title="IRN — print preview" onBack={back}>
        <div style={{ padding: 48 }}>
          <Alert
            type="error"
            showIcon
            message="Could not load IRN"
            description={(error as Error)?.message || 'Check the id and your permissions.'}
          />
        </div>
      </PrintPreviewChrome>
    );
  }

  return (
    <PrintPreviewChrome title="IRN — print preview" fileName={irn.irn_number} onBack={back}>
      <div className="print-doc-a4">
        <InspectionReleaseNotePrintView irn={irn} printedAt={printedAt} resolveUser={resolve} />
      </div>
    </PrintPreviewChrome>
  );
};

export default InspectionReleaseNotePrintPage;
